import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Pencil, Loader2, X, Plus } from 'lucide-react';

/**
 * EditListingDialog — cosmetic-only edits on an active listing.
 *
 * Props:
 *  - open, onOpenChange
 *  - kind: 'hosted' | 'auction'
 *  - listing: full listing object (game or auction)
 *  - onUpdated(fresh): callback with the fresh server-side listing
 *
 * Backend whitelists editable fields, so this component just submits whatever
 * the host changed; backend rejects forbidden keys with rejected_fields[].
 */
export default function EditListingDialog({ open, onOpenChange, kind, listing, onUpdated }) {
  const isAuction = kind === 'auction';
  const titleKey = isAuction ? 'title' : 'room_name';
  const descKey = isAuction ? 'description' : 'prize_description';

  const [form, setForm] = useState({
    [titleKey]: '',
    [descKey]: '',
    physical_shipping_notes: '',
    location: '',
    category: '',
    bin_price: '',
    photo_urls: [],
  });
  const [saving, setSaving] = useState(false);
  const [photoInput, setPhotoInput] = useState('');

  useEffect(() => {
    if (open && listing) {
      setForm({
        [titleKey]: listing[titleKey] || '',
        [descKey]: listing[descKey] || '',
        physical_shipping_notes: listing.physical_shipping_notes || '',
        location: listing.location || '',
        category: listing.category || '',
        bin_price: listing.bin_price != null ? String(listing.bin_price) : '',
        photo_urls: Array.isArray(listing.photo_urls) ? [...listing.photo_urls] : [],
      });
      setPhotoInput('');
    }
  }, [open, listing, titleKey, descKey]);

  const addPhoto = () => {
    const url = photoInput.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Photo URL must start with http(s)://');
      return;
    }
    if (form.photo_urls.length >= 10) {
      toast.error('Max 10 photos');
      return;
    }
    setForm((f) => ({ ...f, photo_urls: [...f.photo_urls, url] }));
    setPhotoInput('');
  };
  const removePhoto = (idx) => {
    setForm((f) => ({ ...f, photo_urls: f.photo_urls.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const id = isAuction ? listing.auction_id : listing.game_id;
      const path = isAuction ? `/auctions/${id}` : `/hosted-games/${id}`;
      const payload = { ...form };
      // BIN price applies only to hosted listings. Convert to number or null.
      if (!isAuction) {
        if (payload.bin_price === '' || payload.bin_price == null) {
          payload.bin_price = null; // revert to default 10×
        } else {
          const n = parseFloat(payload.bin_price);
          if (Number.isNaN(n) || n <= 0) {
            toast.error('BIN price must be a positive number, or leave blank');
            setSaving(false);
            return;
          }
          payload.bin_price = n;
        }
      } else {
        delete payload.bin_price;
      }
      const res = await axios.patch(`${API}${path}`, payload);
      toast.success('Listing updated');
      if (res.data?.rejected_fields?.length) {
        toast.info(`Read-only fields ignored: ${res.data.rejected_fields.join(', ')}`);
      }
      onUpdated?.(res.data?.[isAuction ? 'auction' : 'game'] || null);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-listing-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Pencil className="w-5 h-5 text-amber-400" /> Edit Listing
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Cosmetic changes only. Price, slots, and timing are locked once posted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Title</label>
            <Input
              data-testid="edit-title"
              value={form[titleKey]}
              onChange={(e) => setForm({ ...form, [titleKey]: e.target.value })}
              maxLength={60}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Description</label>
            <Textarea
              data-testid="edit-description"
              value={form[descKey]}
              onChange={(e) => setForm({ ...form, [descKey]: e.target.value })}
              maxLength={600}
              rows={4}
              className="bg-slate-800 border-slate-700 text-white text-sm"
            />
            <p className="text-[10px] text-slate-600 text-right mt-0.5">{(form[descKey] || '').length}/600</p>
          </div>

          {!isAuction && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Shipping notes</label>
              <Textarea
                data-testid="edit-shipping-notes"
                value={form.physical_shipping_notes}
                onChange={(e) => setForm({ ...form, physical_shipping_notes: e.target.value })}
                maxLength={400}
                rows={2}
                placeholder="e.g. Signature required, ships in 2 days"
                className="bg-slate-800 border-slate-700 text-white text-sm"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Location</label>
              <Input
                data-testid="edit-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                maxLength={80}
                placeholder="City, ST"
                className="bg-slate-800 border-slate-700 text-white text-sm h-9"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Category</label>
              <Input
                data-testid="edit-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                maxLength={40}
                placeholder="Cards, NFT, …"
                className="bg-slate-800 border-slate-700 text-white text-sm h-9"
              />
            </div>
          </div>

          {!isAuction && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                Buy-It-Now Price (XRP)
              </label>
              <Input
                data-testid="edit-bin-price"
                type="number"
                min="0"
                step="0.1"
                value={form.bin_price}
                onChange={(e) => setForm({ ...form, bin_price: e.target.value })}
                placeholder={`Default: ${listing?.total_cost ? (listing.total_cost * 10).toFixed(1) : '10×'} XRP`}
                className="bg-slate-800 border-slate-700 text-white text-sm h-9"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">
                Leave blank for default (10× per-slot cost). Allowed: {listing?.total_cost?.toFixed(2) || '—'} to {listing?.prize_value_xrp ? (listing.prize_value_xrp * 5).toFixed(0) : '—'} XRP.
              </p>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Photos ({form.photo_urls.length}/10)</label>
            <div className="space-y-1.5 mb-2">
              {form.photo_urls.map((url, i) => (
                <div key={`${url}-${i}`} className="flex items-center gap-2 bg-slate-800/50 border border-slate-700/40 rounded-md px-2 py-1.5">
                  <img src={url} alt="" className="w-8 h-8 rounded object-cover bg-slate-900 flex-shrink-0" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  <code className="text-[10px] text-slate-400 truncate flex-1">{url}</code>
                  <button
                    data-testid={`remove-photo-${i}`}
                    onClick={() => removePhoto(i)}
                    className="text-slate-500 hover:text-red-400 flex-shrink-0"
                    aria-label="Remove photo"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="edit-photo-input"
                value={photoInput}
                onChange={(e) => setPhotoInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhoto(); } }}
                placeholder="https://…"
                className="bg-slate-800 border-slate-700 text-white text-xs h-8 flex-1"
              />
              <Button
                data-testid="edit-add-photo"
                onClick={addPhoto}
                size="sm"
                disabled={!photoInput.trim() || form.photo_urls.length >= 10}
                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 h-8 px-2"
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <Button
            data-testid="edit-submit"
            onClick={submit}
            disabled={saving}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-10 mt-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
          <p className="text-[10px] text-slate-500 text-center">
            Editing doesn't notify buyers. Entry price &amp; timing remain locked.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
